/**
 * PageSpeedOpportunities - Shows Lighthouse audit opportunities for improvement
 */

import { AlertTriangle, Info, CheckCircle2, Clock, HardDrive } from 'lucide-react'
import type { PageSpeedOpportunity } from './types'

interface PageSpeedOpportunitiesProps {
  opportunities: PageSpeedOpportunity[]
}

export function PageSpeedOpportunities({ opportunities }: PageSpeedOpportunitiesProps) {
  if (opportunities.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 bg-green-500/10 rounded-lg">
        <CheckCircle2 className="h-5 w-5 text-green-500" />
        <span className="text-sm font-medium text-green-600 dark:text-green-400">
          No major opportunities found - site is well optimised
        </span>
      </div>
    )
  }

  // Group by severity (score-based)
  const critical = opportunities.filter((o) => o.score !== null && o.score < 0.5)
  const moderate = opportunities.filter(
    (o) => o.score !== null && o.score >= 0.5 && o.score < 0.9
  )

  return (
    <div className="space-y-4">
      {critical.length > 0 && (
        <OpportunityGroup
          title="High Impact"
          opportunities={critical}
          icon={<AlertTriangle className="h-4 w-4" />}
          variant="warning"
        />
      )}

      {moderate.length > 0 && (
        <OpportunityGroup
          title="Moderate Impact"
          opportunities={moderate}
          icon={<Info className="h-4 w-4" />}
          variant="info"
        />
      )}
    </div>
  )
}

interface OpportunityGroupProps {
  title: string
  opportunities: PageSpeedOpportunity[]
  icon: React.ReactNode
  variant: 'warning' | 'info'
}

function OpportunityGroup({ title, opportunities, icon, variant }: OpportunityGroupProps) {
  const bgClass =
    variant === 'warning'
      ? 'bg-yellow-500/10 border-yellow-500/20'
      : 'bg-blue-500/10 border-blue-500/20'

  const iconClass =
    variant === 'warning' ? 'text-yellow-500' : 'text-blue-500'

  const titleClass =
    variant === 'warning'
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-blue-600 dark:text-blue-400'

  return (
    <div className={`rounded-lg border p-3 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={iconClass}>{icon}</span>
        <span className={`font-medium text-sm ${titleClass}`}>
          {title} ({opportunities.length})
        </span>
      </div>
      <ul className="space-y-2 ml-6">
        {opportunities.map((opportunity) => (
          <li key={opportunity.id} className="text-sm">
            <div className="flex items-start justify-between gap-2">
              <span className="text-foreground">{opportunity.title}</span>
              {opportunity.savings && (
                <SavingsBadge savings={opportunity.savings} />
              )}
            </div>
            {opportunity.displayValue && (
              <span className="text-xs text-muted-foreground block mt-0.5">
                {opportunity.displayValue}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function SavingsBadge({ savings }: { savings: string }) {
  // Determine if this is time or size savings
  const isTime = savings.includes('s') || savings.includes('ms')

  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-xs text-muted-foreground flex-shrink-0">
      {isTime ? (
        <Clock className="h-3 w-3" />
      ) : (
        <HardDrive className="h-3 w-3" />
      )}
      {savings}
    </span>
  )
}
